import { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { apiService } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Tags, 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2,
  Users,
  Heart,
  Home,
  Shield,
  Sparkles,
  Star
} from 'lucide-react';
import { toast } from 'sonner';

// Color options for categories
const COLOR_OPTIONS = [
  { value: '#f59e0b', label: 'Laranja' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#10b981', label: 'Verde' },
  { value: '#ec4899', label: 'Rosa' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#06b6d4', label: 'Ciano' },
  { value: '#84cc16', label: 'Lima' },
];

// Icon options
const ICON_OPTIONS = [
  { value: 'users', label: 'Pessoas', icon: Users },
  { value: 'heart', label: 'Coração', icon: Heart },
  { value: 'home', label: 'Casa', icon: Home },
  { value: 'shield', label: 'Escudo', icon: Shield },
  { value: 'sparkles', label: 'Estrelas', icon: Sparkles },
  { value: 'star', label: 'Estrela', icon: Star },
];

const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(i => i.value === iconName);
  return found ? found.icon : Tags;
};

export const CategoriasPage = () => {
  const { igrejaAtual } = useApp();
  
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    cor: '#f59e0b',
    icone: 'users'
  });

  useEffect(() => {
    if (igrejaAtual?.id) {
      loadCategorias();
    }
  }, [igrejaAtual?.id]);

  const loadCategorias = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCategorias(igrejaAtual.id);
      setCategorias(data);
    } catch (error) {
      console.error('Error loading categorias:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (categoria = null) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setFormData({
        nome: categoria.nome,
        descricao: categoria.descricao || '',
        cor: categoria.cor,
        icone: categoria.icone || 'users'
      });
    } else {
      setEditingCategoria(null);
      setFormData({
        nome: '',
        descricao: '',
        cor: '#f59e0b',
        icone: 'users'
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.nome.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    try {
      setSaving(true);
      
      if (editingCategoria) {
        await apiService.updateCategoria(igrejaAtual.id, editingCategoria.id, formData);
        toast.success('Categoria atualizada!');
      } else {
        await apiService.createCategoria(igrejaAtual.id, formData);
        toast.success('Categoria criada!');
      }
      
      setIsDialogOpen(false);
      loadCategorias();
    } catch (error) {
      console.error('Error saving categoria:', error);
      toast.error('Erro ao salvar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoriaId) => {
    try {
      await apiService.deleteCategoria(igrejaAtual.id, categoriaId);
      toast.success('Categoria removida!');
      loadCategorias();
    } catch (error) {
      console.error('Error deleting categoria:', error);
      toast.error('Erro ao remover categoria');
    }
  };

  if (!igrejaAtual) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Tags className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Nenhuma igreja selecionada</h2>
          <p className="text-muted-foreground">Selecione uma igreja para gerenciar categorias.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition" data-testid="categorias-page">
      {/* Header */}
      <div className="px-6 lg:px-8 py-8 bg-gradient-to-r from-purple-500/10 via-primary/10 to-pink-500/10 border-b border-border">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary text-primary-foreground">
                <Tags className="w-8 h-8" />
              </div>
              <div>
                <h1 className="font-heading text-3xl lg:text-4xl font-bold text-foreground">
                  Categorias
                </h1>
                <p className="text-muted-foreground">
                  Organize suas células por categorias
                </p>
              </div>
            </div>
            
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => handleOpenDialog()} data-testid="nova-categoria-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Categoria
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingCategoria 
                      ? 'Atualize as informações da categoria' 
                      : 'Crie uma nova categoria para organizar suas células'}
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Ex: Jovens, Casais, Homens..."
                      data-testid="categoria-nome-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="descricao">Descrição</Label>
                    <Textarea
                      id="descricao"
                      value={formData.descricao}
                      onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                      placeholder="Descreva o público-alvo desta categoria..."
                      rows={2}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Cor</Label>
                    <div className="flex flex-wrap gap-2">
                      {COLOR_OPTIONS.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, cor: color.value })}
                          className={`w-8 h-8 rounded-full border-2 transition-transform ${
                            formData.cor === color.value 
                              ? 'border-foreground scale-110' 
                              : 'border-transparent'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.label}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Ícone</Label>
                    <div className="flex flex-wrap gap-2">
                      {ICON_OPTIONS.map((icon) => {
                        const IconComp = icon.icon;
                        return (
                          <button
                            key={icon.value}
                            type="button"
                            onClick={() => setFormData({ ...formData, icone: icon.value })}
                            className={`w-10 h-10 rounded-lg flex items-center justify-center border-2 transition-all ${
                              formData.icone === icon.value 
                                ? 'border-primary bg-primary/10' 
                                : 'border-border hover:border-primary/50'
                            }`}
                            title={icon.label}
                          >
                            <IconComp className="w-5 h-5" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Preview */}
                  <div className="pt-4 border-t">
                    <Label className="text-muted-foreground text-sm">Preview</Label>
                    <div className="mt-2 flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                        style={{ backgroundColor: formData.cor }}
                      >
                        {(() => {
                          const IconComp = getIconComponent(formData.icone);
                          return <IconComp className="w-5 h-5" />;
                        })()}
                      </div>
                      <div>
                        <p className="font-medium">{formData.nome || 'Nome da Categoria'}</p>
                        {formData.descricao && (
                          <p className="text-sm text-muted-foreground">{formData.descricao}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="salvar-categoria-btn">
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    {editingCategoria ? 'Salvar' : 'Criar'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-8 py-8">
        <div className="max-w-5xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : categorias.length === 0 ? (
            <Card className="card-elevated">
              <CardContent className="py-12 text-center">
                <Tags className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">Nenhuma categoria</h3>
                <p className="text-muted-foreground mb-4">
                  Crie categorias para organizar suas células
                </p>
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Criar primeira categoria
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {categorias.map((categoria, index) => {
                const IconComp = getIconComponent(categoria.icone);
                return (
                  <Card 
                    key={categoria.id} 
                    className="card-elevated animate-scale-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                    data-testid={`categoria-card-${categoria.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
                          style={{ backgroundColor: categoria.cor }}
                        >
                          <IconComp className="w-6 h-6" />
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleOpenDialog(categoria)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remover categoria?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  A categoria "{categoria.nome}" será removida. 
                                  As células associadas não serão afetadas.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(categoria.id)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Remover
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                      
                      <h3 className="font-semibold text-lg mb-1">{categoria.nome}</h3>
                      {categoria.descricao && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {categoria.descricao}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
